import { userStorage } from '@/configs/storages';

declare global {
  interface Window {
    showDirectoryPicker: (options?: {
      mode?: 'read' | 'readwrite';
    }) => Promise<FileSystemDirectoryHandle>;
  }

  interface FileSystemDirectoryHandle {
    entries: () => AsyncIterableIterator<
      [string, FileSystemDirectoryHandle | FileSystemFileHandle]
    >;
  }
}

export async function exportOpfsToDirectory() {
  const baseDirName = 'halo-backup-' + Date.now();
  const opfsRoot = await navigator.storage.getDirectory();
  const rootDir = await window.showDirectoryPicker({ mode: 'readwrite' });
  const outDir = await rootDir.getDirectoryHandle(baseDirName, { create: true });

  interface BackupManifestFile {
    path: string;
    size: number;
    lastModified: string;
  }

  interface BackupManifest {
    version: 1;
    exportedAt: string;
    files: BackupManifestFile[];
  }

  const manifest: BackupManifest = {
    version: 1,
    exportedAt: new Date().toISOString(),
    files: [],
  };

  async function ensureDir(root: FileSystemDirectoryHandle, parts: string[]) {
    let dir = root;
    for (const p of parts) dir = await dir.getDirectoryHandle(p, { create: true });
    return dir;
  }

  async function walk(opfsDir: FileSystemDirectoryHandle, prefixParts: string[] = []) {
    for await (const [name, handle] of opfsDir.entries()) {
      console.log(name);
      if (handle.kind === 'directory') {
        await walk(handle, [...prefixParts, name]);
      } else {
        const fileHandle = handle as FileSystemFileHandle;
        const file = await fileHandle.getFile();
        const relParts = [...prefixParts, name];
        const relPath = relParts.join('/');

        const parentOut = await ensureDir(outDir, prefixParts);
        const outFileHandle = await parentOut.getFileHandle(name, { create: true });
        const writable = await outFileHandle.createWritable();
        await file.stream().pipeTo(writable);

        manifest.files.push({
          path: relPath,
          size: file.size,
          lastModified: new Date(file.lastModified).toISOString(),
        });
      }
    }
  }

  await walk(opfsRoot);

  const payload = {
    version: 1,
    exportedAt: new Date().toISOString(),
    localStorage: dumpStorage(window.localStorage),
    sessionStorage: dumpStorage(window.sessionStorage),
  };

  const storageHandler = await outDir.getFileHandle('storage.json', {
    create: true,
  });
  const sw = await storageHandler.createWritable();
  await sw.write(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
  await sw.close();

  const manifestHandle = await outDir.getFileHandle('manifest.json', {
    create: true,
  });
  const mw = await manifestHandle.createWritable();
  await mw.write(new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' }));
  await mw.close();
}

export async function importDirectoryToOpfs() {
  const opfsRoot = await navigator.storage.getDirectory();
  const inDir = await window.showDirectoryPicker({ mode: 'read' });

  const manifestHandle = await inDir.getFileHandle('manifest.json');
  const manifestFile = await manifestHandle.getFile();
  const manifest = JSON.parse(await manifestFile.text()) as {
    version: number;
    files: Array<{ path: string }>;
  };

  const storageHandler = await inDir.getFileHandle('storage.json');
  const storageFile = await storageHandler.getFile();
  const storage = JSON.parse(await storageFile.text()) as {
    version: number;
    localStorage?: Record<string, string | null>;
    sessionStorage?: Record<string, string | null>;
  };

  importWebStorage(storage);

  async function ensureOpfsDir(parts: string[]) {
    let dir = opfsRoot;
    for (const p of parts) dir = await dir.getDirectoryHandle(p, { create: true });
    return dir;
  }

  async function getFileHandleByPath(
    rootDir: FileSystemDirectoryHandle,
    parts: string[]
  ): Promise<FileSystemFileHandle> {
    let dir = rootDir;
    for (let i = 0; i < parts.length - 1; i++) {
      dir = await dir.getDirectoryHandle(parts[i], { create: false });
    }
    return await dir.getFileHandle(parts[parts.length - 1], { create: false });
  }

  for (const f of manifest.files) {
    const parts = f.path.split('/').filter(Boolean);
    const name = parts[parts.length - 1];
    const parentParts = parts.slice(0, -1);

    const srcHandle = await getFileHandleByPath(inDir, parts);
    const srcFile = await srcHandle.getFile();

    const opfsParent = await ensureOpfsDir(parentParts);
    const dstHandle = await opfsParent.getFileHandle(name, { create: true });
    const writable = await dstHandle.createWritable();
    await srcFile.stream().pipeTo(writable);
  }
}

function dumpStorage(storage: Storage): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k != null) out[k] = storage.getItem(k);
  }
  return out;
}

function restoreStorage(
  storage: Storage,
  data: Record<string, string | null> | undefined,
  { clearFirst = false }: { clearFirst?: boolean } = {}
) {
  if (clearFirst) storage.clear();
  for (const [k, v] of Object.entries(data || {})) {
    if (v === null || v === undefined) storage.removeItem(k);
    else storage.setItem(k, String(v));
  }
}

function importWebStorage(
  payload: {
    version: number;
    localStorage?: Record<string, string | null>;
    sessionStorage?: Record<string, string | null>;
  },
  opts: { clearLocal?: boolean; clearSession?: boolean } = {
    clearLocal: false,
    clearSession: false,
  }
) {
  if (!payload || payload.version !== 1) throw new Error('Invalid backup payload');

  restoreStorage(window.localStorage, payload.localStorage, { clearFirst: !!opts.clearLocal });
  restoreStorage(window.sessionStorage, payload.sessionStorage, {
    clearFirst: !!opts.clearSession,
  });
}

export async function importOpfs() {
  userStorage.setItem('import_OPFS_flag', true);
  location.reload();
}
